import { Box, Typography, Autocomplete, TextField } from "@mui/material";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RestaurantType } from "src/business/restaurant/restaurant";
import { radiusInput } from "src/design/tokens";

const NON_ID_SEGMENTS = new Set([
  "no-restaurant",
  "restaurant-list",
  "statistics",
  "add-restaurant",
  "admin-dashboard",
  "admin-control-panel",
  "my-organization",
]);

interface RestaurantAutocompleteProps {
  restaurants: RestaurantType[];
  placeholder?: string;
  handleHomeEndKeys?: boolean;
  inputRootStyles?: Record<string, string | number>;
}

export const RestaurantAutocomplete = ({
  restaurants,
  placeholder,
  handleHomeEndKeys = false,
  inputRootStyles,
}: RestaurantAutocompleteProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { restaurantId } = useParams<{ restaurantId?: string }>();

  const value = restaurants.find((r) => r.id === restaurantId) ?? null;

  // An account managing none — an owner who unsubscribed — gets no picker at all
  // rather than one that only ever answers "no options".
  if (restaurants.length === 0) return null;

  const handleChange = (_: React.SyntheticEvent, r: RestaurantType | null) => {
    if (r) {
      const parts = location.pathname.split("/");
      const currentSegment = parts[2];

      if (currentSegment && !NON_ID_SEGMENTS.has(currentSegment)) {
        parts[2] = r.id;
        navigate(parts.join("/"));
      } else {
        navigate(`/restaurant/${r.id}`);
      }
    } else if (restaurants[0]) {
      navigate(`/restaurant/${restaurants[0].id}`);
    }
  };

  return (
    <Autocomplete<RestaurantType, false, false>
      size="small"
      options={restaurants}
      value={value}
      getOptionLabel={(r) => `${r.name} — ${r.city} · ${r.quartier}`}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      filterOptions={(options, { inputValue }) => {
        const q = inputValue.split(" — ")[0].toLowerCase().trim();
        if (!q) return options;
        return options.filter(
          (r) =>
            r.name.toLowerCase().includes(q) ||
            r.city.toLowerCase().includes(q) ||
            r.quartier.toLowerCase().includes(q),
        );
      }}
      onChange={handleChange}
      disableClearable={false}
      handleHomeEndKeys={handleHomeEndKeys}
      renderOption={(props, r) => (
        <li {...props} key={r.id}>
          <Box sx={{ display: "flex", flexDirection: "column", py: 0.25 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
              {r.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", lineHeight: 1.3 }}
            >
              {r.city} · {r.quartier}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder ?? t("sideBar.selectRestaurant")}
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: radiusInput,
              fontSize: 14,
              ...inputRootStyles,
            },
          }}
        />
      )}
    />
  );
};
